import { useTranslation } from 'react-i18next'
import { useEffect, useRef, useState } from 'react'
import { Document, Page } from 'react-pdf'
import { Loader2 } from 'lucide-react'

interface PDFThumbnailSidebarProps {
  file: string | ArrayBuffer
  currentPage: number
  totalPages: number
  onPageSelect: (page: number) => void
}

export default function PDFThumbnailSidebar({
  file,
  currentPage,
  totalPages,
  onPageSelect
}: PDFThumbnailSidebarProps) {
  const { t } = useTranslation()
  const thumbnailRefs = useRef<{ [key: number]: HTMLButtonElement | null }>({})
  const containerRef = useRef<HTMLDivElement>(null)
  const [renderedThumbnails, setRenderedThumbnails] = useState<Set<number>>(new Set())

  const handleThumbnailRenderSuccess = (pageNumber: number) => {
    setRenderedThumbnails(prev => new Set(prev).add(pageNumber))
  }

  useEffect(() => {
    const thumbnailElement = thumbnailRefs.current[currentPage]
    const container = containerRef.current

    if (thumbnailElement && container) {
      const containerRect = container.getBoundingClientRect()
      const thumbnailRect = thumbnailElement.getBoundingClientRect()

      const isVisible =
        thumbnailRect.top >= containerRect.top &&
        thumbnailRect.bottom <= containerRect.bottom

      if (!isVisible) {
        const containerHeight = container.clientHeight
        const thumbnailTop = thumbnailElement.offsetTop
        const thumbnailHeight = thumbnailElement.clientHeight

        const newScrollTop = thumbnailTop - containerHeight / 2 + thumbnailHeight / 2

        container.scrollTo({
          top: newScrollTop,
          behavior: 'smooth'
        })
      }
    }
  }, [currentPage])

  return (
    <div ref={containerRef} className="w-48 bg-base-100 border-r border-base-300 overflow-y-auto h-full">
      <div className="p-2 space-y-2">
        <Document
          file={file}
          loading={
            <div className="flex items-center justify-center h-32 bg-base-200">
              <Loader2 className="w-6 h-6 animate-spin text-base-content/50" />
            </div>
          }
        >
          {Array.from({ length: totalPages }, (_, index) => {
            const pageNumber = index + 1
            const isCurrentPage = pageNumber === currentPage

            return (
              <button
                key={pageNumber}
                ref={(el) => {
                  thumbnailRefs.current[pageNumber] = el
                }}
                onClick={() => onPageSelect(pageNumber)}
                className={`w-full p-1.5 rounded-lg border transition-all overflow-hidden cursor-pointer ${
                  isCurrentPage
                    ? 'border-primary bg-primary/10 ring-2 ring-primary/30'
                    : 'border-base-300 hover:border-primary/50 hover:bg-base-200'
                }`}
              >
                <div className="flex justify-center relative">
                  <Page
                    pageNumber={pageNumber}
                    width={152}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    onRenderSuccess={() => handleThumbnailRenderSuccess(pageNumber)}
                    loading={
                      <div className="flex items-center justify-center w-[152px] h-[215px] bg-base-200">
                        <Loader2 className="w-4 h-4 animate-spin text-base-content/50" />
                      </div>
                    }
                  />
                  {!renderedThumbnails.has(pageNumber) && (
                    <div className="absolute inset-0 flex items-center justify-center bg-base-200">
                      <Loader2 className="w-4 h-4 animate-spin text-base-content/50" />
                    </div>
                  )}
                </div>
                <div className="mt-1 text-xs font-medium text-center text-base-content/70">
                  {t('pdfViewer.sidebar.page', { page: pageNumber })}
                </div>
              </button>
            )
          })}
        </Document>
      </div>
    </div>
  )
}
